import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { useAdmin } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Megaphone, MessageCircle, Send, Trash2, ChevronDown, ChevronUp, LogIn, Mail, Coffee, Star, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diff = (now - d.getTime()) / 1000;
    if (diff < 60) return "now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
};

export default function Community() {
  const { isRTL, lang } = useI18n();
  const { user } = useAuth();
  const isAdmin = useAdmin();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["community-feed"],
    queryFn: endpoints.communityFeed,
  });
  const items = data?.items || [];

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="community-page">
      <Seo
        title={isRTL ? "مجتمع علم القهوة المختصة" : "Coffee Science Community"}
        description={isRTL
          ? "مجتمع باريستا القهوة المختصة العرب — رسائل مباشرة من عبدالله الكاساني، نقاشات حول التحميص والاستخلاص، أسئلة وأجوبة مع المختصين."
          : "The Arab specialty coffee community — direct messages from Abdullah Cassani, deep discussions on roasting & extraction, Q&A with practitioners."}
        keywords="مجتمع القهوة, منتدى القهوة المختصة, نقاشات قهوة, coffee community, barista forum"
        path="/community"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <header className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C86240]/10 text-[#C86240] text-[10px] uppercase tracking-[0.2em] mb-3">
            <Megaphone className="w-3 h-3" /> {isRTL ? "قناة المجتمع" : "Community channel"}
          </div>
          <h1 className={`text-3xl sm:text-4xl font-semibold text-[#2A2421] ${isRTL ? "font-serif-ar" : "font-display"}`}>
            {isRTL ? "تواصلوا معي مباشرة" : "Direct messages from me"}
          </h1>
          <p className="text-sm text-[#5C544F] mt-2 max-w-xl">
            {isRTL
              ? "هنا أنشر إعلانات ورسائل خاصة بالمجتمع، وتظهر تلقائياً وصفات أعضاء المجتمع. تقدر ترد على أي منشور."
              : "I post announcements here, and community recipes shared by members appear automatically. You can reply to any post."}
          </p>
        </header>

        {isAdmin && <AdminComposer onCreated={() => qc.invalidateQueries({ queryKey: ["community-feed"] })} />}

        <div className="space-y-5 mt-6" data-testid="community-feed-list">
          {isLoading && <div className="text-sm text-[#5C544F]">…</div>}
          {!isLoading && items.length === 0 && (
            <div className="rounded-2xl bg-white border border-[#2A2421]/10 p-10 text-center text-[#5C544F]" data-testid="community-feed-empty">
              {isRTL ? "لا توجد منشورات بعد." : "No posts yet."}
            </div>
          )}
          {items.map((it) => (
            it.type === "recipe" ? (
              <SharedRecipeCard key={`r-${it.id}`} item={it} lang={lang} isRTL={isRTL} isAdmin={isAdmin} qc={qc} />
            ) : (
              <BroadcastCard
                key={`b-${it.id}`}
                broadcast={it}
                currentUser={user}
                isAdmin={isAdmin}
                lang={lang}
                isRTL={isRTL}
              />
            )
          ))}
        </div>
      </div>
    </div>
  );
}

function AdminComposer({ onCreated }) {
  const { isRTL } = useI18n();
  const [titleAr, setTitleAr] = useState("");
  const [bodyAr, setBodyAr] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [bodyEn, setBodyEn] = useState("");
  const [bilingual, setBilingual] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!titleAr.trim() && !bodyAr.trim() && !titleEn.trim() && !bodyEn.trim()) {
      toast.error(isRTL ? "اكتب شيئًا أولًا" : "Write something first");
      return;
    }
    setSubmitting(true);
    try {
      const res = await endpoints.adminCreateBroadcast({
        title_ar: titleAr,
        body_ar: bodyAr,
        title_en: bilingual ? titleEn : titleAr,
        body_en: bilingual ? bodyEn : bodyAr,
        notify_email: notifyEmail,
      });
      if (notifyEmail) {
        const sent = res.email_notified || 0;
        const failed = res.email_failed || 0;
        toast.success(
          isRTL
            ? `تم النشر · أُرسل إيميل إلى ${sent}${failed ? ` (فشل ${failed})` : ""}`
            : `Posted · Email sent to ${sent}${failed ? ` (${failed} failed)` : ""}`
        );
      } else {
        toast.success(isRTL ? "تم نشر الرسالة" : "Broadcast posted");
      }
      setTitleAr("");
      setBodyAr("");
      setTitleEn("");
      setBodyEn("");
      onCreated?.();
    } catch (e) {
      toast.error(isRTL ? "فشل النشر" : "Failed to post");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-[#C86240]/40 p-5 shadow-sm" data-testid="admin-broadcast-composer">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Megaphone className="w-4 h-4 text-[#C86240]" />
        <div className="font-semibold text-[#2A2421]">{isRTL ? "إرسال رسالة للمجتمع" : "Post to community"}</div>
        <label className="ms-auto text-xs flex items-center gap-1.5 text-[#5C544F] cursor-pointer">
          <input
            type="checkbox"
            checked={bilingual}
            onChange={(e) => setBilingual(e.target.checked)}
            data-testid="admin-broadcast-bilingual-toggle"
          />
          {isRTL ? "ثنائي اللغة" : "Bilingual"}
        </label>
      </div>
      <Input
        value={titleAr}
        onChange={(e) => setTitleAr(e.target.value)}
        placeholder={isRTL ? "عنوان (اختياري)" : "Title (optional)"}
        className="mb-2 bg-[#F9F6F0]"
        data-testid="admin-broadcast-title-ar"
      />
      <Textarea
        value={bodyAr}
        onChange={(e) => setBodyAr(e.target.value)}
        rows={3}
        placeholder={isRTL ? "اكتب رسالتك…" : "Write your message…"}
        className="mb-2 bg-[#F9F6F0] resize-none"
        data-testid="admin-broadcast-body-ar"
      />
      {bilingual && (
        <>
          <Input
            value={titleEn}
            onChange={(e) => setTitleEn(e.target.value)}
            placeholder="English title (optional)"
            className="mb-2 bg-[#F9F6F0]"
            dir="ltr"
            data-testid="admin-broadcast-title-en"
          />
          <Textarea
            value={bodyEn}
            onChange={(e) => setBodyEn(e.target.value)}
            rows={3}
            placeholder="English body…"
            className="mb-2 bg-[#F9F6F0] resize-none"
            dir="ltr"
            data-testid="admin-broadcast-body-en"
          />
        </>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
        <label className="text-xs flex items-center gap-1.5 text-[#5C544F] cursor-pointer">
          <input
            type="checkbox"
            checked={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.checked)}
            data-testid="admin-broadcast-notify-email-toggle"
          />
          <Mail className="w-3.5 h-3.5" />
          {isRTL ? "إرسال إشعار إيميل لكل المستخدمين" : "Email all users a notification"}
        </label>
        <Button
          onClick={submit}
          disabled={submitting}
          className="rounded-full bg-[#C86240] hover:bg-[#C86240]/90 text-white"
          data-testid="admin-broadcast-submit-btn"
        >
          <Send className="w-4 h-4 me-2" />
          {submitting ? (isRTL ? "جارٍ النشر…" : "Posting…") : (isRTL ? "نشر" : "Post")}
        </Button>
      </div>
    </div>
  );
}

function BroadcastCard({ broadcast, currentUser, isAdmin, lang, isRTL }) {
  const qc = useQueryClient();
  const [showReplies, setShowReplies] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const title = lang === "ar" ? broadcast.title_ar : broadcast.title_en;
  const body = lang === "ar" ? broadcast.body_ar : broadcast.body_en;

  const { data: repliesData } = useQuery({
    queryKey: ["broadcast-replies", broadcast.id],
    queryFn: () => endpoints.listBroadcastReplies(broadcast.id),
    enabled: showReplies,
  });
  const replies = repliesData?.items || [];

  const deleteBroadcast = async () => {
    if (!window.confirm(isRTL ? "حذف الرسالة بالكامل؟" : "Delete this broadcast?")) return;
    try {
      await endpoints.adminDeleteBroadcast(broadcast.id);
      toast.success(isRTL ? "تم الحذف" : "Deleted");
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
      qc.invalidateQueries({ queryKey: ["community-feed"] });
    } catch {
      toast.error(isRTL ? "فشل الحذف" : "Delete failed");
    }
  };

  const sendReply = async () => {
    const t = replyText.trim();
    if (!t) return;
    setSending(true);
    try {
      await endpoints.replyToBroadcast(broadcast.id, t);
      setReplyText("");
      qc.invalidateQueries({ queryKey: ["broadcast-replies", broadcast.id] });
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
      qc.invalidateQueries({ queryKey: ["community-feed"] });
      setShowReplies(true);
    } catch (e) {
      const msg = e?.response?.status === 401
        ? (isRTL ? "سجّل دخولك أولًا" : "Please sign in first")
        : (isRTL ? "فشل الإرسال" : "Failed to send");
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const removeReply = async (replyId) => {
    if (!window.confirm(isRTL ? "حذف الرد؟" : "Delete reply?")) return;
    try {
      await endpoints.deleteBroadcastReply(broadcast.id, replyId);
      qc.invalidateQueries({ queryKey: ["broadcast-replies", broadcast.id] });
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
      qc.invalidateQueries({ queryKey: ["community-feed"] });
    } catch {
      toast.error(isRTL ? "فشل الحذف" : "Delete failed");
    }
  };

  return (
    <article className="rounded-2xl bg-white border border-[#2A2421]/10 p-5" data-testid={`broadcast-card-${broadcast.id}`}>
      <header className="flex items-start gap-3 mb-3">
        {broadcast.admin_picture ? (
          <img src={broadcast.admin_picture} alt="" className="w-10 h-10 rounded-full border" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#C86240] text-white flex items-center justify-center text-sm font-semibold">
            {(broadcast.admin_name || "A").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[#2A2421]">{broadcast.admin_name || "Admin"}</span>
            <span className="text-[10px] uppercase tracking-widest text-[#C86240] bg-[#C86240]/10 px-1.5 py-0.5 rounded-full">
              {isRTL ? "إدارة" : "Admin"}
            </span>
            <span className="text-xs text-[#5C544F]">· {fmtDate(broadcast.created_at)}</span>
          </div>
          {title && <h3 className="text-lg font-semibold text-[#2A2421] mt-1">{title}</h3>}
        </div>
        {isAdmin && (
          <button
            onClick={deleteBroadcast}
            className="text-[#5C544F] hover:text-red-600 p-1"
            data-testid={`broadcast-delete-${broadcast.id}`}
            aria-label="delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </header>
      {body && (
        <div className="text-[#2A2421] whitespace-pre-wrap leading-relaxed text-sm" data-testid={`broadcast-body-${broadcast.id}`}>
          {body}
        </div>
      )}
      <div className="mt-4 flex items-center gap-3 border-t border-[#2A2421]/8 pt-3">
        <button
          onClick={() => setShowReplies((v) => !v)}
          className="text-sm text-[#5C544F] hover:text-[#C86240] inline-flex items-center gap-1"
          data-testid={`broadcast-toggle-replies-${broadcast.id}`}
        >
          <MessageCircle className="w-4 h-4" />
          <span>{broadcast.reply_count || 0} {isRTL ? "رد" : "replies"}</span>
          {showReplies ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {showReplies && (
        <div className="mt-3 space-y-2" data-testid={`broadcast-replies-${broadcast.id}`}>
          {replies.map((r) => (
            <div key={r.id} className="flex items-start gap-2.5 p-3 rounded-xl bg-[#F9F6F0]" data-testid={`reply-${r.id}`}>
              {r.user_picture ? (
                <img src={r.user_picture} alt="" className="w-7 h-7 rounded-full" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-[#2A2421] text-white flex items-center justify-center text-xs font-semibold">
                  {(r.user_name || "U").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs">
                  <span className="font-semibold text-[#2A2421]">{r.user_name || "User"}</span>
                  <span className="text-[#5C544F]"> · {fmtDate(r.created_at)}</span>
                </div>
                <div className="text-sm text-[#2A2421] whitespace-pre-wrap mt-0.5">{r.text}</div>
              </div>
              {(isAdmin || (currentUser && currentUser.user_id === r.user_id)) && (
                <button
                  onClick={() => removeReply(r.id)}
                  className="text-[#5C544F] hover:text-red-600 p-0.5"
                  data-testid={`reply-delete-${r.id}`}
                  aria-label="delete reply"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          {!replies.length && (
            <div className="text-xs text-[#5C544F] text-center py-3">
              {isRTL ? "كن أول من يرد" : "Be the first to reply"}
            </div>
          )}
          {currentUser ? (
            <div className="flex items-start gap-2 pt-2" data-testid={`broadcast-reply-composer-${broadcast.id}`}>
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={2}
                placeholder={isRTL ? "اكتب ردك…" : "Write a reply…"}
                className="bg-white resize-none flex-1 text-sm"
                data-testid={`broadcast-reply-input-${broadcast.id}`}
              />
              <Button
                onClick={sendReply}
                disabled={sending || !replyText.trim()}
                className="rounded-full bg-[#2A2421] hover:bg-[#2A2421]/90 text-[#F9F6F0]"
                data-testid={`broadcast-reply-send-${broadcast.id}`}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="text-center py-3">
              <Button asChild variant="outline" size="sm" className="rounded-full" data-testid="broadcast-signin-cta">
                <Link to="/"><LogIn className="w-3.5 h-3.5 me-1.5" /> {isRTL ? "سجّل دخولك للرد" : "Sign in to reply"}</Link>
              </Button>
            </div>
          )}
        </div>
      )}
    </article>
  );
}


const METHOD_LABEL_AR = {
  v60: "V60", chemex: "Chemex", espresso: "إسبريسو", aeropress: "إيروبريس",
  frenchpress: "فرنش بريس", moka: "موكا", "iced-v60": "V60 مثلج", "iced-drip": "تقطير مثلج",
};

function SharedRecipeCard({ item, lang, isRTL, isAdmin, qc }) {
  const title = (lang === "ar" ? item.title_ar : item.title_en) || item.title_ar || item.title_en;
  const notes = (lang === "ar" ? item.notes_ar : item.notes_en) || "";
  const methodLabel = isRTL ? (METHOD_LABEL_AR[item.method] || item.method) : (item.method || "").toUpperCase();
  const hasRating = (item.ratings_count || 0) > 0;
  const link = `/recipes?recipe=${encodeURIComponent(item.id)}`;
  const [deleting, setDeleting] = React.useState(false);
  const onDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = window.confirm(
      isRTL
        ? `حذف هذه الوصفة نهائياً؟\n«${title}» — ${item.author_name}\nلا يمكن التراجع.`
        : `Delete this recipe permanently?\n"${title}" by ${item.author_name}\nThis cannot be undone.`
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await endpoints.deleteRecipe(item.id);
      toast.success(isRTL ? "تم حذف الوصفة" : "Recipe deleted");
      qc?.invalidateQueries({ queryKey: ["community-feed"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isRTL ? "فشل الحذف" : "Delete failed"));
    } finally {
      setDeleting(false);
    }
  };
  return (
    <article
      className="rounded-2xl bg-white border border-[#2A2421]/10 p-5 hover:shadow-sm transition-shadow"
      data-testid={`shared-recipe-card-${item.id}`}
    >
      <header className="flex items-start gap-3 mb-3">
        {item.author_picture ? (
          <img src={item.author_picture} alt="" className="w-10 h-10 rounded-full border" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#4A5D4E] text-white flex items-center justify-center text-sm font-semibold">
            {(item.author_name || "U").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[#2A2421]">{item.author_name}</span>
            <span className="text-[10px] uppercase tracking-widest text-[#4A5D4E] bg-[#4A5D4E]/10 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1">
              <Coffee className="w-3 h-3" /> {isRTL ? "شارك وصفة" : "Shared a recipe"}
            </span>
            <span className="text-xs text-[#5C544F]">· {fmtDate(item.created_at)}</span>
          </div>
          {title && <h3 className="text-lg font-semibold text-[#2A2421] mt-1 leading-snug">{title}</h3>}
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="text-rose-500 hover:bg-rose-50 rounded-lg p-2 transition-colors disabled:opacity-40"
            title={isRTL ? "حذف الوصفة (إدمن)" : "Delete recipe (admin)"}
            data-testid={`admin-delete-recipe-${item.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </header>

      <Link to={link} className="block group" data-testid={`shared-recipe-link-${item.id}`}>
        <div className="flex gap-4 items-stretch">
          {item.image_url ? (
            <img
              src={item.image_url}
              alt=""
              className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-xl border border-[#2A2421]/10 flex-shrink-0"
            />
          ) : (
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl bg-[#C86240]/8 border border-[#2A2421]/10 flex items-center justify-center flex-shrink-0">
              <Coffee className="w-8 h-8 text-[#C86240]/50" />
            </div>
          )}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              {item.method && (
                <span className="text-[10px] font-semibold uppercase tracking-widest text-[#C86240]">
                  {methodLabel}
                </span>
              )}
              {item.ratio && (
                <span className="text-[10px] text-[#5C544F]">· 1:{item.ratio}</span>
              )}
              {item.coffee_g && item.water_g && (
                <span className="text-[10px] text-[#5C544F]">· {item.coffee_g}g → {item.water_g}g</span>
              )}
              {hasRating && (
                <span className="text-[10px] inline-flex items-center gap-0.5 text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">
                  <Star className="w-3 h-3 fill-amber-400 stroke-amber-400" />
                  {Number(item.avg_rating || 0).toFixed(1)}
                  <span className="text-[#5C544F]">({item.ratings_count})</span>
                </span>
              )}
            </div>
            {notes && (
              <p className="text-sm text-[#5C544F] leading-relaxed line-clamp-3" data-testid={`shared-recipe-notes-${item.id}`}>
                {notes}
              </p>
            )}
            <div className="mt-auto pt-2 flex items-center gap-1 text-xs text-[#C86240] group-hover:underline">
              {isRTL ? "افتح الوصفة" : "Open recipe"}
              <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? "rotate-180" : ""}`} />
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}
